import React, { useEffect } from "react";
import { useHookstate } from "@hookstate/core";
import { Button, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import Layout from "~/components/Layout";
import InternalContent from "~/components/Layout/components/InternalContent";
import InternalHeader from "~/components/Layout/components/InternalHeader";
import { useLang } from "~/hooks/useLang";
import type { IWarehouseCheckout } from "../interface";
import { createWarehouseCheckin, getWarehouseCheckoutById } from "../services";
import type { ICheckinParams } from "../interface/checkin";
import EquipmentsCheckin from "../components/EquipmentsCheckin";
import ResponsiblesCheckin from "../components/ResponsiblesCheckin";
import MaterialCheckin from "../components/MaterialCheckin";

// eslint-disable-next-line max-lines-per-function
export default function CheckinWarehouse() {
  const { translate } = useLang()
  const history = useHistory()
  const { id } = useParams<ICheckinParams>()
  const warehouseCheckout = useHookstate<IWarehouseCheckout | null>(null)
  const loading = useHookstate<boolean>(false)
  const hasSubmitted = useHookstate<boolean>(false)

  useEffect(() => {
    if (!Number(id)) return
    loading.set(true)
    getWarehouseCheckoutById(Number(id))
      .then((response) => {
        warehouseCheckout.set(response)
      }).finally(() => loading.set(false))
  }, [])

  const handleCreateCheckin = () => {
    if (!Number(id)) return false

    hasSubmitted.set(true)

    const checkoutEquipments = warehouseCheckout.get()?.checkoutEquipments ?? []
    const hasInvalidEquipment = checkoutEquipments.some(
      (equipment) => (equipment.disposed || equipment.lost || equipment.returnedDamaged) && !equipment.notes
    )
    if (hasInvalidEquipment) return

    const equipments = checkoutEquipments.map((equipment) => ({
      id: equipment.id,
      equipment_id: equipment.equipment.id,
      lost: equipment.lost,
      disposed: equipment.disposed,
      returned_damaged: equipment.returnedDamaged,
      notes: equipment.notes
    }))

    const materials = warehouseCheckout.get()?.checkoutMaterials.map((material) => ({
      id: material.id,
      material_id: material.material.id,
      quantity_at_checkin: material.quantityAtCheckin,
      notes: material.notes
    })) ?? null

    createWarehouseCheckin({ id: Number(id), equipments, materials })
      .then((response) => {
        if (response.status === 200) {
          history.push("/warehouse-checkout")
        }
    })
  }

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={translate('Return')} />
      <InternalContent expandButton={false}>
        {!loading.get() ?
          <>
            <ResponsiblesCheckin warehouseCheckout={warehouseCheckout} />
            <Divider />
            <EquipmentsCheckin warehouseCheckout={warehouseCheckout} hasSubmitted={hasSubmitted.get()} />
            <MaterialCheckin warehouseCheckout={warehouseCheckout} hasSubmitted={hasSubmitted.get()} />
            <Grid container display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push("/warehouse-checkout")}
              >
                {translate('Cancel')}
              </Button>
              <Button
                onClick={() => handleCreateCheckin()}
                variant="contained"
                color="primary"
              >
                {translate('Save')}
              </Button>
            </Grid>
          </>
          : <CircularProgress />}
      </InternalContent>
    </Layout>
  )
}